import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback, } from 'react';
import * as Styled from './Tabs.style';
import { generateUniqueId } from '../../../utils/idUtils';
import ChevronLeftIcon from '../../../assets/icons/ChevronLeftIcon';
import ChevronRightIcon from '../../../assets/icons/ChevronRightIcon';
import { devices } from '../../../base/styled/variables.styled';
import { Button } from '../Button/Button';
export const Tabs = ({ items, defaultActiveKey = null, highlighted = false, 
// showAllItems = false,
isInverted = false, isVertical = false, 
// tabIcons = [],
showNavButtons = false, prevLabel, nextLabel, onChange, noContentPadding = false, }) => {
    const [instanceId] = useState(() => generateUniqueId('tabs-instance'));
    const [activeKey, setActiveKey] = useState(() => defaultActiveKey !== null && defaultActiveKey !== void 0 ? defaultActiveKey : (items.length > 0 ? items[0].key : null));
    const [focusedKey, setFocusedKey] = useState(activeKey);
    // const [hoveredKey, setHoveredKey] = useState<string | number | null>(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const tabsListRef = useRef(null);
    const tabItemRefs = useRef({});
    const [isCarouselMode, setIsCarouselMode] = useState(false);
    const generateTabId = (key) => `tab-${instanceId}-${key}`;
    const generatePanelId = (key) => `panel-${instanceId}-${key !== null && key !== void 0 ? key : 'no-key'}`;
    const checkScrollability = useCallback((listElement, currentCarouselMode) => {
        if (!listElement || !currentCarouselMode) {
            setCanScrollPrev(false);
            setCanScrollNext(false);
            return;
        }
        const tolerance = 1;
        const isTablet = window.matchMedia(devices.tablets).matches;
        const isMobile = window.matchMedia(devices.mobile).matches;
        const isVerticalRow = isVertical && (isTablet || isMobile);
        if (isVerticalRow || !isVertical) {
            setCanScrollPrev(listElement.scrollLeft > tolerance);
            setCanScrollNext(listElement.scrollLeft < listElement.scrollWidth - listElement.clientWidth - tolerance);
        }
        else {
            setCanScrollPrev(listElement.scrollTop > tolerance);
            setCanScrollNext(listElement.scrollTop < listElement.scrollHeight - listElement.clientHeight - tolerance);
        }
    }, [isVertical]);
    useEffect(() => {
        const handleResize = () => {
            const listElement = tabsListRef.current;
            if (isVertical) {
                const isTablet = window.matchMedia(devices.tablets).matches;
                const isMobile = window.matchMedia(devices.mobile).matches;
                const isVerticalRow = isTablet || isMobile;
                let shouldBeCarousel = false;
                if (listElement && isVerticalRow) {
                    const tolerance = 1;
                    const hasHorizontalOverflow = listElement.scrollWidth > listElement.clientWidth + tolerance;
                    shouldBeCarousel = hasHorizontalOverflow;
                }
                else {
                    shouldBeCarousel = false;
                }
                setIsCarouselMode(shouldBeCarousel);
                if (listElement) {
                    checkScrollability(listElement, shouldBeCarousel);
                }
                return;
            }
            const numItems = items.length;
            const shouldBeCarousel = numItems > 2;
            setIsCarouselMode(shouldBeCarousel);
            if (listElement) {
                checkScrollability(listElement, shouldBeCarousel);
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [items.length, isVertical, checkScrollability]);
    useEffect(() => {
        if (defaultActiveKey !== null && defaultActiveKey !== undefined) {
            setActiveKey(defaultActiveKey);
        }
    }, [defaultActiveKey]);
    useEffect(() => {
        if (!isCarouselMode)
            return;
        const activeTabNode = activeKey !== null ? tabItemRefs.current[activeKey] : null;
        if (activeTabNode && tabsListRef.current) {
            const listElement = tabsListRef.current;
            const { offsetLeft, clientWidth: tabWidth } = activeTabNode;
            const { clientWidth: listWidth } = listElement;
            const scrollTarget = offsetLeft - (listWidth / 2) + (tabWidth / 2);
            listElement.scrollTo({
                left: scrollTarget,
                behavior: 'smooth',
            });
            const timer = setTimeout(() => {
                checkScrollability(listElement, isCarouselMode);
            }, 350);
            return () => clearTimeout(timer);
        }
    }, [activeKey, isCarouselMode, checkScrollability]);
    useEffect(() => {
        const listElement = tabsListRef.current;
        if (!listElement)
            return;
        if (isCarouselMode) {
            const handleScroll = () => window.requestAnimationFrame(() => {
                checkScrollability(listElement, isCarouselMode);
            });
            listElement.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                listElement.removeEventListener('scroll', handleScroll);
            };
        }
        else {
            setCanScrollPrev(false);
            setCanScrollNext(false);
        }
    }, [isCarouselMode, checkScrollability]);
    useEffect(() => {
        setFocusedKey(activeKey);
    }, [activeKey]);
    useEffect(() => {
        var _a;
        if (focusedKey !== null && tabItemRefs.current[focusedKey]) {
            (_a = tabItemRefs.current[focusedKey]) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
        }
    }, [focusedKey]);
    const handleTabClick = (key, event) => {
        setActiveKey(key);
        setFocusedKey(key);
        if (onChange) {
            onChange(key);
        }
        const clickedItem = items.find(item => item.key === key);
        if (clickedItem && clickedItem.onClick) {
            clickedItem.onClick(event);
        }
    };
    const handleScroll = (direction) => {
        const listElement = tabsListRef.current;
        if (!listElement)
            return;
        const isTablet = window.matchMedia(devices.tablets).matches;
        const isMobile = window.matchMedia(devices.mobile).matches;
        const isVerticalRow = isVertical && (isTablet || isMobile);
        if (isVerticalRow || !isVertical) {
            const scrollAmount = listElement.clientWidth * 0.8;
            listElement.scrollBy({
                left: direction === 'prev' ? -scrollAmount : scrollAmount,
                behavior: 'smooth',
            });
        }
        else {
            const scrollAmount = listElement.clientHeight * 0.8;
            listElement.scrollBy({
                top: direction === 'prev' ? -scrollAmount : scrollAmount,
                behavior: 'smooth',
            });
        }
    };
    const enabledItems = items.filter(item => !item.disabled);
    const currentIndex = enabledItems.findIndex(item => item.key === activeKey);
    const prevEnabledIndex = currentIndex > 0 ? currentIndex - 1 : null;
    const nextEnabledIndex = currentIndex >= 0 && currentIndex < enabledItems.length - 1 ? currentIndex + 1 : null;
    const goPrev = () => {
        if (prevEnabledIndex !== null) {
            const prevKey = enabledItems[prevEnabledIndex].key;
            const fakeEvent = new MouseEvent('click');
            handleTabClick(prevKey, fakeEvent);
        }
    };
    const goNext = () => {
        if (nextEnabledIndex !== null) {
            const nextKey = enabledItems[nextEnabledIndex].key;
            const fakeEvent = new MouseEvent('click');
            handleTabClick(nextKey, fakeEvent);
        }
    };
    const handleKeyDown = (event) => {
        const target = event.target;
        if (!items || items.length === 0 || target.getAttribute('role') !== 'tab')
            return;
        const enabledItems = items.filter((item) => !item.disabled);
        if (enabledItems.length === 0)
            return;
        let currentFocusIndex = enabledItems.findIndex((item) => item.key === focusedKey);
        if (currentFocusIndex === -1) {
            currentFocusIndex = enabledItems.findIndex((item) => item.key === activeKey);
        }
        if (currentFocusIndex === -1)
            return;
        let nextItemKey = null;
        let newIndex = null;
        switch (event.key) {
            case 'ArrowLeft':
            case 'ArrowUp':
                newIndex = (currentFocusIndex - 1 + enabledItems.length) % enabledItems.length;
                event.preventDefault();
                break;
            case 'ArrowRight':
            case 'ArrowDown':
                newIndex = (currentFocusIndex + 1) % enabledItems.length;
                event.preventDefault();
                break;
            case 'Home':
                newIndex = 0;
                event.preventDefault();
                break;
            case 'End':
                newIndex = enabledItems.length - 1;
                event.preventDefault();
                break;
            case 'Enter':
            case ' ': {
                const fakeEvent = new MouseEvent('click');
                handleTabClick(enabledItems[currentFocusIndex].key, fakeEvent);
                event.preventDefault();
                return;
            }
            case 'Tab':
                if (event.shiftKey) {
                    newIndex = (currentFocusIndex - 1 + enabledItems.length) % enabledItems.length;
                }
                else {
                    newIndex = (currentFocusIndex + 1) % enabledItems.length;
                }
                event.preventDefault();
                break;
            default:
                return;
        }
        if (newIndex !== null) {
            nextItemKey = enabledItems[newIndex].key;
            setFocusedKey(nextItemKey);
        }
    };
    const activeItem = items === null || items === void 0 ? void 0 : items.find((item) => item.key === activeKey);
    const activeContent = activeItem === null || activeItem === void 0 ? void 0 : activeItem.children;
    const content = (_jsxs(Styled.TabsContainer, { onKeyDown: handleKeyDown, className: isVertical ? 'vertical-tabs-container' : '', children: [_jsxs(Styled.CarouselWrapper, { "$highlighted": highlighted, "$isInverted": isInverted, children: [isCarouselMode && canScrollPrev && (_jsx(Styled.PaginationContainer, { direction: "left", "$isInverted": isInverted, children: _jsx(Styled.ArrowButton, { "$direction": "prev", onClick: () => handleScroll('prev'), "aria-label": "Scroll left", "$isInverted": isInverted, children: _jsx(ChevronLeftIcon, {}) }) })), _jsx(Styled.TabsList, { ref: tabsListRef, role: "tablist", "aria-orientation": isVertical && (window.matchMedia(devices.tablets).matches || window.matchMedia(devices.mobile).matches) ? 'horizontal' : isVertical ? 'vertical' : 'horizontal', "$carousel": isCarouselMode, "$highlighted": highlighted, "$isInverted": isInverted, "$vertical": isVertical, children: items.map((item) => (_jsx(Styled.TabItem, { ref: (el) => (tabItemRefs.current[item.key] = el), role: "tab", "aria-selected": activeKey === item.key, "aria-controls": generatePanelId(item.key), id: generateTabId(item.key), tabIndex: activeKey === item.key ? 0 : -1, onClick: (e) => !item.disabled && handleTabClick(item.key, e), disabled: item.disabled, "$isActive": activeKey === item.key, "data-active": activeKey === item.key, "$highlighted": highlighted, "$isInverted": isInverted, "$vertical": isVertical, onFocus: () => setFocusedKey(item.key), children: _jsx(Styled.TabLabelWrapper, { className: "tabs__label__wrapper", children: item.label }) }, item.key))) }), isCarouselMode && canScrollNext && (_jsx(Styled.PaginationContainer, { direction: "right", "$isInverted": isInverted, children: _jsx(Styled.ArrowButton, { "$direction": "next", onClick: () => handleScroll('next'), "aria-label": "Scroll right", "$isInverted": isInverted, children: _jsx(ChevronRightIcon, {}) }) }))] }), _jsx(Styled.TabContent, { role: "tabpanel", id: generatePanelId(activeKey), "aria-labelledby": activeKey ? generateTabId(activeKey) : undefined, "$isInverted": isInverted, "$noPadding": noContentPadding, children: activeContent }), showNavButtons && (_jsxs(Styled.NavButtonsWrapper, { children: [_jsx(Button, { variant: "secondary", onClick: goPrev, disabled: prevEnabledIndex === null, label: prevLabel || 'Voltar' }), _jsx(Button, { onClick: goNext, disabled: nextEnabledIndex === null, label: nextLabel || 'Continuar' })] }))] }));
    if (isVertical) {
        return (_jsx(Styled.VerticalLayout, { "$isInverted": isInverted, children: content }));
    }
    return content;
};
export default Tabs;
//# sourceMappingURL=Tabs.js.map